import { useState, useEffect, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, CheckCircle, AlertTriangle } from 'lucide-react';
import { cn } from '../lib/utils';
import { api } from '../lib/api';

/** Response shape from POST /api/forum-groups */
interface RefreshResponse {
  groupMap: Record<string, string>;
  source: string;
  refreshedAt?: string;
}

/** Toast notification state */
interface ToastState {
  type: 'success' | 'error';
  message: string;
}

/** Visual flash state on the button after mutation */
type FlashType = 'success' | 'error' | null;

/**
 * GroupRefresh — inline button + toast combo that triggers group map
 * refresh from Invision via POST /api/forum-groups.
 *
 * Follows the Refresh Button + Toast visual contract (UI-SPEC.md §3-4):
 * - Gold-accented button with RefreshCw icon (spins during loading)
 * - Disabled during mutation with aria-busy
 * - onSuccess: invalidates ['forum-groups'] query, shows success toast
 * - onError: shows error toast
 * - Toast auto-dismisses after 4 seconds
 * - Brief visual flash on button for success/error feedback
 */
export function GroupRefresh() {
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<ToastState | null>(null);
  const [flash, setFlash] = useState<FlashType>(null);

  // Auto-dismiss toast after 4 seconds
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  // Clear flash after 800ms
  useEffect(() => {
    if (!flash) return;
    const timer = setTimeout(() => setFlash(null), 800);
    return () => clearTimeout(timer);
  }, [flash]);

  const mutation = useMutation<RefreshResponse>({
    mutationFn: () => api<RefreshResponse>('/api/forum-groups', { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['forum-groups'] });
      setToast({ type: 'success', message: 'Groups synced from Invision' });
      setFlash('success');
    },
    onError: () => {
      setToast({ type: 'error', message: "Couldn't refresh — using last known groups" });
      setFlash('error');
    },
  });

  const isPending = mutation.isPending;

  const handleClick = useCallback(() => {
    if (!isPending) mutation.mutate();
  }, [isPending, mutation]);

  return (
    <div>
      {/* Refresh button */}
      <button
        type="button"
        onClick={handleClick}
        disabled={isPending}
        aria-label="Refresh group names from Invision"
        aria-busy={isPending}
        className={cn(
          'eo-btn eo-btn--gold inline-flex items-center gap-2 px-4 py-1 font-mono text-[10.5px] tracking-label uppercase rounded cursor-pointer transition',
          // Flash states (brief visual feedback)
          flash === 'success' && 'border-ok bg-ok/10',
          flash === 'error' && 'border-warn bg-warn/10',
        )}
      >
        <RefreshCw
          size={14}
          className={cn(isPending && 'animate-spin')}
        />
        {isPending ? 'Refreshing…' : 'Refresh Groups'}
      </button>

      {/* Toast notification */}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          className={cn(
            'eo-toast',
            toast.type === 'success' ? 'eo-toast--ok' : 'eo-toast--warn',
          )}
        >
          {toast.type === 'success' ? (
            <CheckCircle size={14} />
          ) : (
            <AlertTriangle size={14} />
          )}
          <span>{toast.message}</span>
        </div>
      )}
    </div>
  );
}
